'use client';

import React, {
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useRef,
} from 'react';
import clsx from 'clsx';
import type { PortalProps } from 'components/Portal/Portal';
import { noop } from 'utils/noop';
import type { EitherLabelOrLabelledBy } from 'utils/types';
import { mergeRefs, useEnterAnimation } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import styles from './Dialog.module.css';
import { DialogContainer } from './DialogContainer';
import { getNormalizedDialogSize } from './DialogSizes';
import {
  type DialogMobileVariant,
  type DialogPositions,
  type DialogSizes,
  type DialogSizesProps,
  type OnRequestClose,
} from './types';

export type DialogProps = {
  /** The content of the dialog. */
  children?: ReactNode;
  /** Control whether the dialog is showing. */
  isShowing: boolean;
  /** A callback to indicate that the dialog should close. */
  onRequestClose?: OnRequestClose;
  /** An optional element ref to focus on mount. Eg., a save button. */
  initialFocusRef?: any;
  /** An optional element ref to return focus to when the dialog closes. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Controls the visibility of the focus ring when the dialog receives focus.
   * This is mainly used for AlertDialogs and cases where you want to highlight
   * an action button that can be triggered via keyboard.
   */
  showFocusRing?: boolean;
  /** Enable clicking on the overlay to close it. */
  enableClickOutsideToClose?: boolean;
  /** A configurable z-index. Generally you shouldn't touch this,
   * because it can have implications for the z-index of subsequent panels.
   */
  zIndex?: number;
  /** @deprecated See @DialogCallbackMigration.md for a migration guide. */
  onEntered?: () => void;
  /** @deprecated See @DialogCallbackMigration.md for a migration guide. */
  onExited?: () => void;
  /** Vertical position within the page where the Dialog will sit. At smaller viewport heights this will be ignored. */
  position?: DialogPositions;
  /** A ref to the dialog element. React 19 only */
  ref?: Ref<HTMLDivElement>;
  /** Disable the dialog entry and exit animations */
  disableAnimation?: boolean;
  /** Mobile-specific presentation: use 'fullscreen' for edge-to-edge on mobile */
  mobileVariant?: DialogMobileVariant;
} & Pick<PortalProps, 'hasOutermostTheme'> &
  HTMLAttributes<HTMLDivElement> &
  Partial<EitherLabelOrLabelledBy> &
  DialogSizesProps;

export function Dialog({
  initialFocusRef,
  returnFocusRef,
  isShowing,
  children,
  onRequestClose = noop,
  zIndex = theme.zIndices.modal,
  size = 'medium',
  enableClickOutsideToClose = true,
  onEntered = noop,
  onExited = noop,
  position = 'centered',
  hasOutermostTheme,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ref: refProp,
  width,
  showFocusRing = false,
  disableAnimation = false,
  mobileVariant = 'default',
  ...other
}: DialogProps) {
  return (
    <DialogContainer
      disableAnimation={disableAnimation}
      enableClickOutsideToClose={enableClickOutsideToClose}
      hasOutermostTheme={hasOutermostTheme}
      initialFocusRef={initialFocusRef}
      isShowing={isShowing}
      mobileVariant={mobileVariant}
      onEntered={onEntered}
      onExited={onExited}
      onRequestClose={onRequestClose}
      position={position}
      returnFocusRef={returnFocusRef}
      showFocusRing={showFocusRing}
      zIndex={zIndex}
    >
      {(ref, { isExiting }, id) => {
        return (
          <DialogWithMountAnimationRef
            ref={mergeRefs(ref, refProp)}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabel ? undefined : ariaLabelledBy || id}
            aria-modal="true"
            disableAnimation={disableAnimation}
            isExiting={isExiting}
            mobileVariant={mobileVariant}
            role="dialog"
            size={size}
            tabIndex={-1}
            width={width}
            {...other}
          >
            {children}
          </DialogWithMountAnimationRef>
        );
      }}
    </DialogContainer>
  );
}

function DialogWithMountAnimation(
  {
    children,
    size,
    isExiting = false,
    width: widthProp,
    style,
    disableAnimation,
    mobileVariant,
    className,
    ...other
  }: {
    size: DialogSizes;
    isExiting: boolean;
    width?: string;
    disableAnimation?: boolean;
    mobileVariant: DialogMobileVariant;
  } & React.HTMLAttributes<HTMLDivElement>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  const ref = useRef<HTMLDivElement>(null);
  const isEntering = useEnterAnimation(ref, !disableAnimation);
  const width = widthProp || getNormalizedDialogSize(size);

  return (
    <div
      ref={mergeRefs(ref, forwardedRef)}
      className={clsx(styles.dialog, className)}
      data-disable-animation={disableAnimation || undefined}
      data-entering={isEntering || undefined}
      data-exiting={isExiting || undefined}
      data-mobile-fullscreen={mobileVariant === 'fullscreen' || undefined}
      data-size={size}
      style={
        {
          ...style,
          '--vapor-dialog-width': width,
        } as React.CSSProperties
      }
      {...other}
    >
      {children}
    </div>
  );
}

const DialogWithMountAnimationRef = React.forwardRef(DialogWithMountAnimation);
